"use client";

import { useState } from "react";
import Link from "next/link";
import SectionHeader from "@/components/ui/section-header";
import { ArrowUpRight, Check, Copy } from "@/components/icons";
import { DOCS, GITHUB_MCP_URL } from "@/lib/constants";
import DevCodeDisplay from "@/components/sections/products/shared/dev-code-display";
import { MOBILE_PLAYWRIGHT_EXAMPLE, type DevLink } from "./data";

const LINKS: DevLink[] = [
  { label: "Mobile proxy docs", href: DOCS.mobileProxies },
  { label: "Mobile API reference", href: DOCS.apiPerGbMobile },
  { label: "MCP server", href: "/tools/mcp-server" },
  { label: "MCP server on GitHub", href: GITHUB_MCP_URL },
  { label: "Playwright proxy setup guide", href: "/blog/playwright-proxy-python-nodejs/" },
  { label: "Browser Use integration guide", href: "/blog/browser-use-residential-proxies/" },
  { label: "Proxy session management for AI agents", href: "/blog/proxy-sessions-for-ai-agents/" },
];

export default function MobileForDevelopers() {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(MOBILE_PLAYWRIGHT_EXAMPLE);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <section
      className="py-section-sm border-border border-b"
      aria-labelledby="mobile-for-developers-heading"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-8 max-w-3xl">
          <SectionHeader
            eyebrow="Built for Developers"
            id="mobile-for-developers-heading"
            heading="Mobile Proxies for Developers"
            body="HTTP, HTTPS, and SOCKS5 carry your agent's traffic through Proxidize mobile IPs. The REST API and MCP server manage those access points — credentials, city rotation — not the traffic. Reach for mobile IPs when a task needs a mobile network identity, like checking a localized landing page."
          />
        </div>

        <div className="border-border bg-card overflow-hidden rounded-2xl border">
          <div className="border-border flex items-center justify-between border-b px-5 py-3">
            <span className="text-muted-foreground bg-foreground/5 rounded px-2 py-0.5 font-mono text-xs font-medium">
              javascript
            </span>
            <button
              type="button"
              onClick={handleCopy}
              className="text-muted-foreground hover:text-foreground -m-2 flex items-center gap-1.5 p-2 text-xs transition-colors"
              aria-label="Copy code"
            >
              {copied ? <Check size={12} /> : <Copy size={12} />}
              {copied ? "Copied!" : "Copy"}
            </button>
          </div>
          <div className="overflow-x-auto">
            <DevCodeDisplay code={MOBILE_PLAYWRIGHT_EXAMPLE} language="javascript" />
          </div>
        </div>

        <p className="text-muted-foreground mt-6 max-w-3xl text-sm leading-relaxed">
          Country targeting is set directly in the username, as shown above. City and session mode
          (rotating or sticky) are configured per access point in the dashboard or Session Builder —
          generate the credentials there and drop them into the snippet.
        </p>

        <p className="text-muted-foreground mt-8 mb-3 text-xs font-bold tracking-wider uppercase">
          Developer resources
        </p>
        <ul
          className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3"
          aria-label="Developer resources"
        >
          {LINKS.map((link) => (
            <li key={link.href}>
              <Link
                href={link.href}
                target={link.href.startsWith("http") ? "_blank" : undefined}
                rel={link.href.startsWith("http") ? "noopener noreferrer" : undefined}
                className="border-border bg-card hover:border-foreground/20 hover:bg-foreground/[0.02] group flex items-center justify-between gap-3 rounded-xl border px-4 py-3 transition-colors"
              >
                <span className="text-foreground text-sm font-medium">{link.label}</span>
                <ArrowUpRight
                  size={14}
                  className="text-muted-foreground group-hover:text-foreground shrink-0 transition-colors"
                  aria-hidden="true"
                />
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
